import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '@/api/axios';
import { ChatCircleText, Plus, Trash, PencilSimple, Star } from '@phosphor-icons/react';

export const TestimoniosManager = ({ productos }) => {
  const keys = Object.keys(productos);
  const [producto, setProducto] = useState(keys[0]);
  const [items, setItems] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState({ nombre: '', lugar: '', texto: '', estrellas: 5 });
  const [editId, setEditId] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const r = await api.get(`/landing/testimonios/${producto}`);
      setItems(r.data.items || []);
    } catch { setItems([]); }
  }, [producto]);

  useEffect(() => { if (abierto) cargar(); }, [abierto, cargar]);

  const guardar = async () => {
    if (!form.nombre.trim() || !form.texto.trim()) { toast.error('Nombre y testimonio son obligatorios'); return; }
    try {
      if (editId) {
        await api.put(`/landing/admin/testimonios/${editId}`, { ...form, producto });
        toast.success('Testimonio actualizado');
      } else {
        await api.post('/landing/admin/testimonios', { ...form, producto });
        toast.success('Testimonio agregado');
      }
      setForm({ nombre: '', lugar: '', texto: '', estrellas: 5 });
      setEditId(null);
      cargar();
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo guardar'); }
  };

  const eliminar = async (t) => {
    if (!window.confirm(`¿Eliminar el testimonio de "${t.nombre}"?`)) return;
    try {
      await api.delete(`/landing/admin/testimonios/${t.id}`);
      toast.success('Testimonio eliminado');
      cargar();
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo eliminar'); }
  };

  const editar = (t) => {
    setEditId(t.id);
    setForm({ nombre: t.nombre, lugar: t.lugar || '', texto: t.texto, estrellas: t.estrellas || 5 });
  };

  return (
    <div className="bg-black/[0.02] border border-current/10 rounded-2xl mt-6" data-testid="testimonios-manager">
      <button onClick={() => setAbierto(!abierto)} data-testid="testimonios-manager-toggle"
        className="w-full flex items-center justify-between px-5 py-4 font-black text-sm hover:bg-black/5 transition-colors rounded-2xl">
        <span className="flex items-center gap-2"><ChatCircleText size={18} className="text-[#1FA3E8]" /> Testimonios de las landings</span>
        <span className="opacity-50 text-xs">{abierto ? 'Cerrar ▲' : 'Administrar ▼'}</span>
      </button>
      {abierto && (
        <div className="px-5 pb-5 space-y-4">
          <select value={producto} onChange={(e) => { setProducto(e.target.value); setEditId(null); }} data-testid="testimonios-producto-select"
            style={{ colorScheme: 'dark' }}
            className="w-full sm:w-72 bg-black/10 border border-current/20 rounded-xl px-3 py-2 text-sm outline-none">
            {keys.map((k) => <option key={k} value={k}>{productos[k]}</option>)}
          </select>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Form */}
            <div className="bg-black/5 border border-current/10 rounded-xl p-4 space-y-3">
              <p className="font-black text-xs uppercase tracking-wide opacity-70">{editId ? '✏️ Editando testimonio' : '➕ Nuevo testimonio'}</p>
              <input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="testimonio-nombre"
                placeholder="Nombre del cliente (Ej: Ing. Juan Pérez)" className="w-full bg-black/10 border border-current/20 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1FA3E8]" />
              <input value={form.lugar} onChange={(e) => setForm({ ...form, lugar: e.target.value })} data-testid="testimonio-lugar"
                placeholder="Lugar (opcional, Ej: Ciudad de Guatemala)" className="w-full bg-black/10 border border-current/20 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1FA3E8]" />
              <textarea value={form.texto} onChange={(e) => setForm({ ...form, texto: e.target.value })} data-testid="testimonio-texto" rows={3}
                placeholder="Opinión del cliente…" className="w-full bg-black/10 border border-current/20 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1FA3E8] resize-none" />
              <div className="flex items-center gap-2">
                <span className="text-xs opacity-60 font-bold">Estrellas:</span>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setForm({ ...form, estrellas: n })} data-testid={`testimonio-estrella-${n}`} type="button">
                    <Star size={18} weight={n <= form.estrellas ? 'fill' : 'regular'} className={n <= form.estrellas ? 'text-amber-400' : 'opacity-30'} />
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={guardar} data-testid="testimonio-guardar"
                  className="flex items-center gap-1.5 bg-[#1FA3E8] hover:brightness-110 text-white font-black text-xs px-4 py-2 rounded-full transition-all">
                  <Plus size={14} /> {editId ? 'Guardar cambios' : 'Agregar testimonio'}
                </button>
                {editId && (
                  <button onClick={() => { setEditId(null); setForm({ nombre: '', lugar: '', texto: '', estrellas: 5 }); }}
                    className="text-xs font-bold opacity-60 hover:text-white px-3">Cancelar</button>
                )}
              </div>
            </div>

            {/* Lista */}
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {items.length === 0 && (
                <p className="opacity-50 text-xs italic p-3">Esta landing usa los testimonios de fábrica. Al agregar el primero, la página mostrará SOLO los de esta lista.</p>
              )}
              {items.map((t) => (
                <div key={t.id} className="bg-black/5 border border-current/10 rounded-xl p-3 flex items-start gap-3" data-testid={`testimonio-item-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-xs">{t.nombre} <span className="text-amber-400">{'★'.repeat(t.estrellas || 5)}</span></p>
                    {t.lugar && <p className="opacity-50 text-[10px]">{t.lugar}</p>}
                    <p className="opacity-70 text-xs mt-1 line-clamp-2">"{t.texto}"</p>
                  </div>
                  <button onClick={() => editar(t)} data-testid={`testimonio-editar-${t.id}`} className="opacity-50 hover:text-[#1FA3E8] p-1" title="Editar"><PencilSimple size={16} /></button>
                  <button onClick={() => eliminar(t)} data-testid={`testimonio-eliminar-${t.id}`} className="opacity-50 hover:text-red-400 p-1" title="Eliminar"><Trash size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
